import {type SuggestObject, SuggestAction} from '@yandex-delivery/api/model/suggest'
import type {AddressSuggestParams as SuggestRequest, SuggestService} from '@yandex-delivery/api/service/suggest'
import {filter, firstOf, get, pipe, raise} from '@yandex-delivery/utils'

import type {AddressPin, AddressPinParams, LocationRequest, SuggestParams} from '../../model/types'
import {toSimpleAddressPin, toUserAddressIntercityPin, toUserAddressPin} from './mappers'

export function createAddressService(suggestService: SuggestService) {
  const {fetchAddressSuggest} = suggestService

  function _fetchSuggest(request: SuggestRequest): Promise<SuggestObject[]> {
    return fetchAddressSuggest(request).then(response => {
      if (request.currentMode === 'delivery_intercity') {
        return pipe(response, get('__origin.results'))
      } else {
        return pipe(
          response,
          get('__origin.results'),
          filter((result: SuggestObject) => result.action !== 'substitute'),
        )
      }
    })
  }

  return {
    fetchAddressSuggest,
    async fetchSuggest({coordinates, text, type, currentMode}: SuggestParams): Promise<SuggestObject[]> {
      const location = coordinates as SuggestRequest['location']

      return _fetchSuggest({
        type: (currentMode === 'delivery_intercity' && type) || 'a',
        location,
        text,
        currentMode: currentMode ?? 'delivery_b2b',
      })
    },
    async fetchAddressPin(params: AddressPinParams): Promise<AddressPin> {
      if ('addressPin' in params) {
        return toUserAddressPin(params.addressPin)
      }

      return _fetchSuggest(toSuggestParams(params)).then(addresses => {
        return pipe(
          addresses,
          firstOf<SuggestObject[], SuggestObject>(
            // выбираем либо единственный адрес из саджеста
            suggest => (suggest.length === 1 ? suggest[0] : undefined),
            // либо тот, по которому уже кликали (из параметров)
            suggest =>
              'suggestObject' in params
                ? suggest.find(address => address.text === params.suggestObject.text)
                : undefined,
            // либо, первый адрес
            suggest => suggest[0],
          ),
          (suggestObject = raise('[fetchAddressPin] "suggestObject" not found')): AddressPin => {
            if ('currentMode' in params && params.currentMode === 'delivery_intercity')
              return toUserAddressIntercityPin(suggestObject)
            return 'initial' in params //
              ? toSimpleAddressPin(suggestObject)
              : toUserAddressPin(suggestObject)
          },
        )
      })
    },
  }
}

function toSuggestParams(params: AddressPinParams): SuggestRequest {
  if ('coordinates' in params) {
    const location = params.coordinates as LocationRequest['location']

    if ('suggestObject' in params) {
      return {
        type: 'a',
        location,
        __origin: params.suggestObject,
        currentMode: params.currentMode ?? 'delivery_b2b',
      }
    }

    return {
      type: 'a',
      location,
      point: location,
      action: SuggestAction.PIN_DROP,
    }
  }

  raise('[toSuggestParams] not implemented yet\n' + JSON.stringify(params, null, 2))
}
